import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MainFormBase } from '../../../../../components/common/main-form-base/main-form-base';
import { PolluxFormConfig } from '../../../../../components/common/pollux-form-config/pollux-form-config';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { TenantsWorkspaceComponent } from '../../workspaces/tenants-workspace/tenants-workspace.component';
import { tenantsFormConfig } from './tenants-form-config';

/**
 * Tenants form
 */
@Component({
  selector: 'tenants-form',
  templateUrl: './tenants-form.component.html',
  styleUrls: ['./tenants-form.component.css']
})
export class TenantsFormComponent extends MainFormBase<any> implements OnInit, OnDestroy {
  /**
   * Show the move to the previous item button
   */
  @Input() showPrevBtn = false;

  /**
   * Show the move to the next item button
   */
  @Input() showNextBtn = false;

  /**
   * Emits an event if the move to previous item button was clicked
   */
  @Output() showPrevious = new EventEmitter<void>();

  /**
   * Emits an event if the move to next item button was clicked
   */
  @Output() showNext = new EventEmitter<void>();

  /**
   * Sets the OID
   */
  @Input() set oid(value: number) {
    this.currentOid = value;
  }

  /**
   * The workspace
   */
  @Input() workspace: TenantsWorkspaceComponent;

  /**
   * The required ref lists
   */
  protected get requiredReflists(): string[] {
    return [
      'ref_unit_type',
      'ref_report_occupancylevel'
    ];
  }

  /**
  * Constructor
  * @param injector The Angular injector
  */
  constructor(protected injector: Injector) {
    super(injector);
  }

  /**
   * Sets the default retriever data
   * @param data
   */
  protected setDefaultRetrieverData(data: any) {
    //Nothing
  }

  /**
   * Gets the required retrievers
   */
  protected get requiredRetrievers(): string[] {
    return [];
  }

  /**
  * Gets the API POST endpoint for saving a single BO
  */
  protected getSaveSingleEndpoint(): string {
    return null;
  }

  /**
  * Gets the API GET endpoint for loading a single BO by it's OID
  * @param oid The BO's OID
  */
  protected getLoadSingleEndpoint(oid: number): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `project/valuation/tenant/details/single?oid=${oid}`);
  }

  /**
   * Gets the workspace reference
   */
  public get workspaceReference(): WorkspaceBase {
    return this.workspace;
  }

  /**
   * Closes the form
   */
  public close(): void {
    this.eventBus.broadcastEvent('closeTenantForm');
  }

  /**
   * Gets the form configuration
   */
  protected get formConfig(): PolluxFormConfig[] {
    return tenantsFormConfig;
  }

  /**
   * Performs post-processing of the data
   * Important: Please do not parse any Dates from string in this method!
   * @param data Loaded data
   */
  protected postProcessData(data: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      try {
        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * If the save call was triggered, this method is called.
   * Use it to inform the user, close the form or trigger grid reloading.
   * @param saveResult Th result returned from the API
   */
  protected handlePostSave(saveResult: any): void {
    this.close();
  }

  /**
   * If an error occurred during saving, this method is called.
   * Use it to re-enable buttons, inform the user, etc.
   * Note: This occurs on errors and doesn't evaluate the saveResult returned from the API, since this can be different between calls.
   * @param error The error information
   */
  protected handleSaveError(error: any): void {
    console.error(error);
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * If the save process was aborted, this methd is called.
   * You can use it to re-enable buttons, inform the user, etc
   * @param _data The data that should be saved originally
   */
  public handleSaveAborted(_data: any): void {
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * Perform any pre-save activities here, for example user interaction, user queries etc.
   * If the Promise is resolved with true, saving will continue. If iit's resolved with false or rejected, saving is cancelled.
   * @param _data The data that should be saved
   */
  public handlePreSave(_data: any): Promise<boolean> {
    return Promise.resolve(true);
  }

  /**
   * If a validation error occurs, this method is called.
   * Use it to inform the user, enable buttons, etc
   * @param _data The data
   */
  public handleValidationError(_data: any) {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      hideConfirmButton: true,
      cancel: () => {
        //Nothing
      }
    }, 'fillRequiredFieldsDialog');
  }

  /**
   * Destroys the component
   */
  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {

    this.onEvents.push(this.eventBus.subscribeEvent('saveTenantData', (_payload, _options) => {
      this.save();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('cancelTenantDataChanges', (_payload, _options) => {
      this.close();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('valuationTabSelect', (_payload, _options) => {
      this.close();
    }));

    this.init().then(() => {
      this.eventBus.broadcastEvent('showSaveCancelNavigationAction', true);
    });
  }

  /**
   * The next utton was clicked
   */
  public nextTenant(): void {
    this.showNext.emit();
  }

  /**
   * The previous button was clicked
   */
  public previousTenant(): void {
    this.showPrevious.emit();
  }

  /**
   * Evaluates the save result. Returns true if saving was successful
   * @param result The save result
   */
  public evaluateSaveResult(result: any): boolean {
    return true;//no saving on tenant form
  }
}
